import type { UserOption as Reporter } from '@atlassian/jira-base-user-picker';
import type {
	ContextAndValuesOfIssueBeingCreated,
	IssueType,
	RequestType,
} from '@atlassian/jira-issue-create-common-types';
import type { User as Assignee } from '@atlassian/jira-issue-field-assignee';
import type { Options as Components } from '@atlassian/jira-issue-field-components-field';
import type { Option as Priotity } from '@atlassian/jira-issue-field-priority';
import type { ResolutionOption as Resolution } from '@atlassian/jira-issue-field-resolution';
import type { VersionOption as Version } from '@atlassian/jira-issue-field-versions';
import {
	ASSIGNEE_TYPE,
	REPORTER_TYPE,
	CMDB_OBJECT_CF_TYPE,
	PRIORITY_TYPE,
	AFFECTS_VERSIONS_TYPE,
	FIX_VERSIONS_TYPE,
	COMPONENTS_TYPE,
	DUE_DATE_TYPE,
	RESOLUTION_TYPE,
	SUMMARY_TYPE,
} from '@atlassian/jira-platform-field-config';
import type { CmdbObject } from '@atlassian/jira-servicedesk-insight-shared-types';

const getStringValue = (fieldValues: string) => fieldValues;
const getJoinedArray = (fieldValues: Version[] | Components) =>
	fieldValues.map(({ value }) => value).join(',');

const extractFieldValueMethods = {
	[SUMMARY_TYPE]: getStringValue,
	[DUE_DATE_TYPE]: getStringValue,
	[AFFECTS_VERSIONS_TYPE]: getJoinedArray,
	[FIX_VERSIONS_TYPE]: getJoinedArray,
	[COMPONENTS_TYPE]: getJoinedArray,
	[ASSIGNEE_TYPE]: ({ accountId }: Assignee) => accountId,
	[REPORTER_TYPE]: ({ id }: Reporter) => id,
	[PRIORITY_TYPE]: ({ value }: Priotity) => value,
	[RESOLUTION_TYPE]: ({ value }: Resolution) => value,
	[CMDB_OBJECT_CF_TYPE]: (fieldValues: CmdbObject[]) =>
		fieldValues.map(({ workspaceId, objectId }) => `${workspaceId}:${objectId}`).join(','),
} as const;

const extractIssueTypeValue = (selectedIssueType: IssueType | RequestType): string =>
	selectedIssueType.type === 'REQUEST_TYPE'
		? selectedIssueType.issueTypeId
		: selectedIssueType.value;

export const extractFieldValueMap = (
	contextAndValues?: ContextAndValuesOfIssueBeingCreated,
): {
	[key: string]: string;
} => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const result: Record<string, any> = {};

	if (contextAndValues) {
		const { getFormState, fieldConfig, selectedProject, selectedIssueType } = contextAndValues;
		const formData = getFormState().values;

		Object.keys(formData).forEach((fieldId) => {
			// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly summary: (fieldValues: string) => string; readonly duedate: (fieldValues: string) => string; readonly versions: (fieldValues: Options | VersionOption[]) => string; ... 6 more ...; readonly "com.atlassian.jira.plugins.cmdb:cmdb-object-cftype": (fieldValues: CmdbObject[]) => string; }'.
			const extractFieldValue = extractFieldValueMethods[fieldConfig[fieldId]?.fieldType];
			const fieldValues = formData[fieldId];

			if (extractFieldValue && fieldValues) {
				result[fieldId] = extractFieldValue(fieldValues);
			}
		});

		if (selectedProject) {
			result.pid = selectedProject.value;
		}

		if (selectedIssueType) {
			result.issuetype = extractIssueTypeValue(selectedIssueType);
		}
	}

	return result;
};
